import React from "react";
import {
  Button,
  DatePicker,
  Input,
  Radio,
  Spin,
  Table,
  Typography,
} from "antd";
import { SaveOutlined, SearchOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import useProgramAttendanceDaily, {
  AttendanceDailyRow,
} from "../../hooks/attendance/useProgramAttendanceDaily";
import useSaveProgramAttendance from "../../hooks/attendance/useSaveProgramAttendance";
import { AttendanceStatus } from "../../../../types/dto/program";

const { Text } = Typography;

interface Props {
  programId: number;
  programActive: boolean;
}

export default function AttendanceDailyTable({
  programId,
  programActive,
}: Props) {
  const [selectedDate, setSelectedDate] = React.useState<Moment>(moment());
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);

  const { loading, rows, setRowStatus, load, hasChanges } =
    useProgramAttendanceDaily(programId, selectedDate);
  const { saving, save } = useSaveProgramAttendance(programId, selectedDate);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedDate]);

  const normalizeSearchText = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const filteredRows = React.useMemo(() => {
    const query = normalizeSearchText(searchQuery);
    if (!query) return rows;
    return rows.filter((row) => {
      const name = normalizeSearchText(row.userName);
      const code = normalizeSearchText(row.studentCode ?? "");
      return name.includes(query) || code.includes(query);
    });
  }, [rows, searchQuery]);

  const handleSave = async () => {
    const ok = await save(rows);
    if (ok) {
      await load();
    }
  };

  const columns = [
    {
      title: "Código",
      dataIndex: "studentCode",
      width: 110,
      render: (val: string) => (
        <Text className="text-xs text-gray-500">{val ?? "—"}</Text>
      ),
    },
    {
      title: "Estudiante",
      dataIndex: "userName",
      width: 220,
    },
    {
      title: "Asistencia",
      key: "attendance",
      render: (_: unknown, row: AttendanceDailyRow) => (
        <Radio.Group
          value={row.attendanceStatus}
          disabled={!programActive}
          onChange={(e) =>
            setRowStatus(row.programStudentId, e.target.value as AttendanceStatus)
          }
        >
          <Radio value="present">Asistió</Radio>
          <Radio value="absent">No asistió</Radio>
        </Radio.Group>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {!programActive && (
        <Text type="warning">
          El programa está inactivo. Puedes consultar registros, pero no guardar
          nuevos cambios.
        </Text>
      )}

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <DatePicker
          value={selectedDate}
          format="DD/MM/YYYY"
          allowClear={false}
          onChange={(date) => date && setSelectedDate(date)}
        />
        <Input
          placeholder="Buscar por nombre o código"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 280 }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={!programActive || !hasChanges}
          onClick={handleSave}
        >
          Guardar asistencia
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          rowKey="programStudentId"
          columns={columns}
          dataSource={filteredRows}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["15", "20", "50", "100"],
            showTotal: (total) => `${total} estudiantes`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          scroll={{ x: 600 }}
        />
      </Spin>
    </div>
  );
}
